import type React from 'react'
import FolderIcon from '@mui/icons-material/FolderOpen'
import AddTaskIcon from '@mui/icons-material/AddTask'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'

import * as MUI from '@mui/material'

import { Box, Chip, IconButton, Menu, MenuItem, Tooltip, Typography, Button } from '@mui/material'

import { cloneElement, useState } from 'react'
import type { FC, MouseEventHandler } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { queueNotification } from '~/shared/notifications/actions'

import {
  useAddCase,
  useTaskCollectionPreviewMutation,
  useUpdateCase,
  useCase,
} from '~/entities/case'
import { useUpdatePhoneCall } from '../PhoneCallService'
import { findMessageIdentifier, createCaseFromCategory, linkCaseToMessage } from './utils'

import { isUrgent } from '../chatUtils'
import { CaseOrCategory, isCase } from './TriageCaseOrCategorySelect/types'
import TriageCaseOrCategorySelectContainer from './TriageCaseOrCategorySelect/Container'
import { useUpdateChatMessage } from '../ChatService'
import { useMutation } from '~/shared/hooks/query'
import { queryClient } from '~/shared/lib/queryClient'
import { UpdateMessageUrgency, logEvent } from '~/shared/lib/events'
import { FFModal } from '~/shared/ui'
import { useManageSelectedTodo } from '../useManageSelectedTodo'
import {
  BffCrmPersonChatRetrieveResponse,
  chatV2ChatMessageDelete,
  ProvidersCasesCreateResponse,
} from '@fireflyhealth/api'
import { assertPropertyIsNotNull } from '~/shared/lib/types'
import type { TranscriptMessage } from './selection/clipboard'
import { useChat } from '../CommunicationThread'
import { MeState } from '~/app/store/reducers/me'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { logger } from '~/shared/lib/logger'

type Communication =
  BffCrmPersonChatRetrieveResponse['communications']['results'][number]['content_object']

type ChatCommunication = Extract<Communication, { kind: 'chat' }>

type PhoneCommunication = Extract<Communication, { kind: 'phone' }>

interface Props {
  person: Pick<BffCrmPersonChatRetrieveResponse['person'], 'id' | 'user'>
  message:
    | (Pick<
        ChatCommunication,
        | 'kind'
        | 'id'
        | 'uid'
        | 'message_tags'
        | 'is_urgent_message'
        | 'event'
        | 'text'
        | 'sent_at'
        | 'attachment'
        | 'file_attachment'
      > & {
        sender: Pick<ChatCommunication['sender'], 'id' | 'avatar_url' | 'full_name_used'>
        case_relations: {
          case: Pick<ChatCommunication['case_relations'][number]['case'], 'id'>
        }[]
      })
    | (Pick<PhoneCommunication, 'kind' | 'id'> & {
        created_by: Pick<PhoneCommunication['created_by'], 'id'>
        handled_by: Pick<NonNullable<PhoneCommunication['handled_by']>, 'id'> | null
        case_relations: {
          case: Pick<ChatCommunication['case_relations'][number]['case'], 'id'>
        }[]
      })
  setIsLoading: (value: boolean) => void
  isLastMessage: boolean
  threadUid: string | null
  canCase: boolean
}

const getMessageUrgency = (isUrgentMessage: boolean) => {
  return isUrgentMessage ? 'urgent' : 'not urgent'
}

const useToggleMessageUrgency = (me: Pick<MeState, 'provider_fields'>, person: Props['person']) => {
  const { mutateAsync: handleUpdateChatMessage } = useUpdateChatMessage()
  const role = me.provider_fields?.internal_role ? me.provider_fields?.internal_role : null

  return useMutation(
    (message: Pick<ChatCommunication, 'is_urgent_message' | 'uid'>) => {
      const isUrgentMessage = !isUrgent(message)
      return handleUpdateChatMessage({
        uid: message.uid,
        is_urgent_message: isUrgentMessage,
      })
    },
    {
      onSuccess: (_data, message) => {
        void logEvent<UpdateMessageUrgency>('UPDATED_MESSAGE_URGENCY_FOR_CHAT_MESSAGE', {
          messageUid: message.uid,
          previousMessageUrgency: getMessageUrgency(isUrgent(message)),
          revisedMessageUrgency: getMessageUrgency(!isUrgent(message)),
          userRoleTitle: role,
        })
        void queryClient.invalidateQueries({ queryKey: ['chat', person.id] })
      },
    },
  )
}

const DeleteChatMessageModal: FC<{
  messageText: string
  loading: boolean
  setModalOpen: (value: boolean) => void
  modalOpen: boolean
  onModalConfirm: () => void
}> = ({ messageText, loading, setModalOpen, modalOpen, onModalConfirm }) => {
  let abbreviatedMessageText = messageText.substring(0, 27)

  if (abbreviatedMessageText.length < messageText.length) {
    abbreviatedMessageText += '...'
  }

  if (!modalOpen) return null

  return (
    <FFModal
      open={true}
      header={
        <Box sx={{ m: 2 }}>
          <Typography variant="h5">
            Delete Message: <i>{abbreviatedMessageText}</i>
          </Typography>
        </Box>
      }
      footer={
        <Box sx={{ m: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setModalOpen(false)} disabled={loading}>
            Cancel
          </Button>
        </Box>
      }
    >
      <>
        <Box sx={{ m: 2 }}>
          <Typography>
            Are you sure you want to delete this message? The patient may have already received a
            notification about your message.
          </Typography>
        </Box>
        <Box sx={{ m: 2 }}>
          <Button onClick={onModalConfirm} disabled={loading} variant="contained">
            Yes, Delete
          </Button>
        </Box>
      </>
    </FFModal>
  )
}

const MessageActions: FC<Props> = props => {
  // Message information and controls for the actions a user can take on a message
  // e.g. triaging to Clinical vs. Operational, adding to a Case
  const { message } = props
  const me = useSelector(state => state.me)
  const senderId = 'sender' in message ? message.sender.id : null
  const mine = me.id === senderId
  const isEvent = 'event' in message && Boolean(message.event)

  // Get selection state from context
  const { selection, case: case_ } = useChat()

  // Quoting is only safe inside a focused case view (URL has `caseId`), where
  // every visible message belongs to the same thread. In the default view the
  // selection can span multiple threads/cases and the quote save fails.
  const isFocusedCaseView = case_ != null

  const { mutate: toggleMessageUrgency } = useToggleMessageUrgency(me, props.person)

  // Controls for the "More" menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const menuIsOpen = Boolean(menuAnchorEl)

  const closeMenu = () => {
    setMenuAnchorEl(null)
    setInlineSelectMode(null)
  }

  const [inlineSelectMode, setInlineSelectMode] = useState<'case' | 'quote' | null>(null)
  const messageIdentifier = findMessageIdentifier(props.message)
  const { mutate: handleUpdatePhoneCall } = useUpdatePhoneCall(props.person.id ?? null)

  const taskCollectionPreviewMutation = useTaskCollectionPreviewMutation()
  const { mutateAsync: handleUpdatePatientCase } = useUpdateCase()
  const { mutateAsync: handleAddPatientCase } = useAddCase()
  const { selectedCaseId, navigateToCase } = useManageSelectedTodo()
  const dispatch = useDispatch()

  const handleTriageMessage = async (selectedOption: CaseOrCategory) => {
    // Only works for Persons with a User, though a Person without one could have phone calls
    // and emails triaged too.
    assertPropertyIsNotNull(props.person, 'user')

    props.setIsLoading(true)
    try {
      if (isCase(selectedOption)) {
        // Link existing case to message
        if (messageIdentifier) {
          await linkCaseToMessage(selectedOption, messageIdentifier, handleUpdatePatientCase)
        }
      } else {
        // Create new case from category
        const addedCase = await createCaseFromCategory(
          selectedOption,
          props.person,
          me,
          taskCollectionPreviewMutation,
          handleAddPatientCase,
          messageIdentifier,
        )
        handleAfterLinkCase(addedCase)
      }
    } finally {
      props.setIsLoading(false)
    }
  }

  const handleAfterLinkCase = (caseObj: ProvidersCasesCreateResponse) => {
    if (caseObj) {
      setInlineSelectMode(null)
      props.setIsLoading(false)
      navigateToCase(caseObj.id, { type: 'add' })
    }
  }

  const handleToggleMessageUrgency = () => {
    closeMenu()
    if (message.kind === 'chat') toggleMessageUrgency(message)
  }

  const handleRemovePhoneCall = (phoneCallId: number) => {
    closeMenu()
    // Defensive guard: the render-time gate may be evaluating stale chat data
    // if a CaseRelation was added (e.g. via drag-and-drop) while the menu was
    // already open. The backend rejects this with a 400, but bailing here gives
    // a clearer message and avoids the spurious PATCH.
    if (message.kind === 'phone' && message.case_relations.length > 0) {
      dispatch(
        queueNotification({
          message: 'Unlink the case from the phone call before removing it.',
          variant: 'error',
        }),
      )
      return
    }
    handleUpdatePhoneCall({
      id: phoneCallId,
      person: null,
    })
  }

  const canDeleteMessage = mine && props.isLastMessage && !(props.message.kind === 'phone')

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)

  const { mutateAsync: deleteMutate, isPending: deleteIsLoading } = useMutation(
    async () => {
      if (!(props.message.kind === 'chat')) {
        return
      }
      await chatV2ChatMessageDelete.destroy({
        uid: props.message.uid,
      })
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['chat', props.person.id] })
      },
    },
    { success: 'Message deleted', error: 'Failed to delete message' },
  )

  const handleDelete = async () => {
    try {
      await deleteMutate()
    } catch (e) {
      logger.error('[Chat] delete message', e)
    } finally {
      setDeleteModalOpen(false)
    }
  }

  const menuItems = (() => {
    const items: React.JSX.Element[] = []

    if (!isEvent) {
      if (message.kind === 'phone') {
        // Check if current user handled the call (for Talkdesk) OR created it (for manual calls)
        const isOwnCall = (message.handled_by?.id ?? message.created_by.id) == me.id
        if (!message.case_relations.length && isOwnCall) {
          items.push(
            <MenuItem onClick={() => handleRemovePhoneCall(message.id)}>
              Remove phone call
            </MenuItem>,
          )
        }
      } else {
        if (message.kind === 'chat') {
          items.push(
            <MenuItem onClick={() => handleToggleMessageUrgency()}>
              {isUrgent(message) ? 'Remove "Urgent" tag' : 'Tag as "Urgent"'}
            </MenuItem>,
          )
        }
      }
    }

    if (canDeleteMessage) {
      items.push(
        <MenuItem
          onClick={() => {
            closeMenu()
            setDeleteModalOpen(true)
          }}
        >
          Delete
        </MenuItem>,
      )
    }

    return items.map((item, i) => cloneElement(item, { key: i }))
  })()

  // Get the linked case ID from message.case_relations (if any)
  const linkedCaseId = message.case_relations?.[0]?.case?.id ?? null
  // Fetch the linked case to get ownerGroup for the "Add Task" action
  const { data: linkedCase } = useCase(linkedCaseId, () => {
    if (selectedCaseId) navigateToCase(null)
  })

  const handleInlineTriageClick = () => {
    setInlineSelectMode('case')
  }

  const handleInlineQuoteClick: MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation()
    setInlineSelectMode('quote')
  }

  // "Add Task" button: directly create task for the linked case (no dropdown)
  const handleAddTaskClick: MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation()
    if (linkedCase) {
      navigateToCase(
        linkedCase.id,
        {
          type: 'createForwardTask',
          // Only the id and text travel: the payload is serialised into the URL, and those are
          // the fields the forwarded-task draft reads.
          message: { id: message.id, text: message.kind === 'chat' ? message.text : null },
          ownerGroup: linkedCase.owner_group ?? null,
          timestamp: Date.now(),
        },
        { preserveChatHighlight: true },
      )
    }
  }

  // Build a QuotedMessage from the current message (only for chat messages)
  const buildQuotedMessageFromCurrent = (): TranscriptMessage | null => {
    if (message.kind !== 'chat') return null
    let attachmentType: 'image' | 'file' | null = null
    if (message.attachment) {
      attachmentType = 'image'
    } else if (message.file_attachment) {
      attachmentType = 'file'
    }
    // Note: relations are not included in the Props type, so we pass an empty array
    // When quoting a single message via the menu, we don't have access to relations data
    return {
      object_id: message.id,
      content_type: 'chatmessagev2',
      sender_name: message.sender.full_name_used,
      sent_at: message.sent_at ?? new Date().toISOString(),
      text: message.text ?? '',
      is_mine: message.sender.id === me.id,
      avatar_url: message.sender.avatar_url,
      attachment_type: attachmentType,
      quoted_messages: [],
    }
  }

  // Get quoted messages: use all selected messages if available, otherwise fall back to current message
  const getQuotedMessages = (): TranscriptMessage[] => {
    // If there are selected messages, use those
    const selectedMessages = selection.getSelectedMessagesForClipboard()
    if (selectedMessages.length > 0) {
      return selectedMessages
    }
    // Otherwise fall back to just the current message
    const current = buildQuotedMessageFromCurrent()
    return current ? [current] : []
  }

  // "Quote" button handler: after selecting a case, navigate to it with the quoted messages
  const handleQuoteSelect = async (selectedOption: CaseOrCategory) => {
    const quotedMessages = getQuotedMessages()
    if (quotedMessages.length === 0) return // Quote only works for chat messages

    props.setIsLoading(true)
    try {
      if (isCase(selectedOption)) {
        const caseObj = selectedOption
        setInlineSelectMode(null)
        // Navigate to the selected case and populate the quoted messages in chat input
        navigateToCase(caseObj.id, {
          type: 'quoteMessage',
          quotedMessages,
          timestamp: Date.now(),
        })
      } else {
        // Only works for Persons with a User; see handleTriageMessage.
        assertPropertyIsNotNull(props.person, 'user')

        const addedCase = await createCaseFromCategory(
          selectedOption,
          props.person,
          me,
          taskCollectionPreviewMutation,
          handleAddPatientCase,
          messageIdentifier,
          undefined,
          { linkMessageToCase: false },
        )
        setInlineSelectMode(null)
        // Navigate to the new case and populate the quoted messages in chat input
        navigateToCase(addedCase.id, {
          type: 'quoteMessage',
          quotedMessages,
          timestamp: Date.now(),
        })
      }
    } finally {
      props.setIsLoading(false)
    }
  }

  const multipleSelected = selection.selectedCount > 1

  return (
    <Box
      sx={{
        opacity: inlineSelectMode || menuIsOpen ? 1 : 0,
        position: 'absolute',
        right: 0,
        top: -10,
        border: '1px solid',
        padding: 0.5,
        borderColor: 'secondary.100',
        backgroundColor: 'white',
        borderRadius: '10px',
        zIndex: 99,
      }}
      className="message-actions-hover-control"
    >
      {inlineSelectMode ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            position: 'relative',
            top: '.7rem',
            width: '250px',
            '& .MuiAutocomplete-root': {
              width: 'inherit',
            },
            '& .MuiAutocomplete-root .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
              width: 'inherit',
            },
          }}
        >
          <TriageCaseOrCategorySelectContainer
            message={props.message}
            person={props.person}
            filterCarePlanCases={false}
            excludeAlreadyLinkedCases={inlineSelectMode === 'quote' ? false : undefined}
            onChange={option =>
              fireAndForget('[Chat] link message to case', () =>
                inlineSelectMode === 'quote'
                  ? handleQuoteSelect(option)
                  : handleTriageMessage(option),
              )
            }
            onBlur={() => setInlineSelectMode(null)}
            threadUid={props.threadUid}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', position: 'relative', flexGrow: 1, alignItems: 'center' }}>
          {isEvent ? null : (
            <Box sx={{ cursor: 'pointer' }}>
              {/* Add Task button: only shown when message is linked to a case */}
              {linkedCaseId && !multipleSelected ? (
                <Tooltip title="Add Task">
                  <Chip
                    icon={<AddTaskIcon style={{ color: MUI.colors.blue[500] }} />}
                    variant={'outlined'}
                    size="small"
                    onClick={handleAddTaskClick}
                    sx={{
                      color: 'primary.main',
                      borderColor: 'transparent',
                      backgroundColor: 'transparent',
                      '& .MuiChip-icon': {
                        marginRight: -2,
                      },
                    }}
                  />
                </Tooltip>
              ) : null}
              {props.canCase ? (
                <Tooltip title="Case">
                  <Chip
                    icon={<FolderIcon style={{ color: MUI.colors.blue[500] }} />}
                    variant={'outlined'}
                    size="small"
                    onClick={handleInlineTriageClick}
                    sx={{
                      color: 'primary.main',
                      borderColor: 'transparent',
                      backgroundColor: 'transparent',
                      '& .MuiChip-icon': {
                        marginRight: -2,
                      },
                    }}
                  />
                </Tooltip>
              ) : null}
              {/* Quote button (focused case view only) */}
              {!props.canCase && isFocusedCaseView ? (
                <Tooltip title="Quote">
                  <Chip
                    icon={
                      <MUI.Badge
                        badgeContent={selection.selectedCount}
                        color="primary"
                        invisible={selection.selectedCount === 0}
                      >
                        <FormatQuoteIcon style={{ color: MUI.colors.blue[500] }} />
                      </MUI.Badge>
                    }
                    variant={'outlined'}
                    size="small"
                    onClick={handleInlineQuoteClick}
                    sx={{
                      color: 'primary.main',
                      borderColor: 'transparent',
                      backgroundColor: 'transparent',
                      '& .MuiChip-icon': {
                        marginRight: -2,
                      },
                    }}
                  />
                </Tooltip>
              ) : null}
            </Box>
          )}
          {menuItems.length ? (
            <IconButton
              size="small"
              title="More..."
              onClick={event => setMenuAnchorEl(event.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
          ) : null}

          <Menu
            anchorEl={menuAnchorEl}
            open={menuIsOpen}
            onClose={() => closeMenu()}
            slotProps={{
              paper: {
                sx: { minWidth: '100px', filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))' },
              },
            }}
          >
            {menuItems}
          </Menu>
        </Box>
      )}

      {canDeleteMessage ? (
        <DeleteChatMessageModal
          onModalConfirm={() => void handleDelete()}
          modalOpen={deleteModalOpen}
          setModalOpen={setDeleteModalOpen}
          messageText={'text' in message ? (message.text ?? '') : ''}
          loading={deleteIsLoading}
        />
      ) : null}
    </Box>
  )
}

export default MessageActions
